import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Users } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { browserLang, langName, LANGS, MAX_PARTICIPANTS } from "@/lib/langs";
import { useAuth } from "@/context/AuthContext";
import { LangSelect } from "@/components/LangSelect";
import { AuroraLogo, AuroraWordmark } from "@/components/AuroraLogo";
import { NoTraceBanner } from "@/components/room/Privacy";

export const RoomEntry = ({ room, onJoined }) => {
  const { user } = useAuth();
  const mine = room.participants.find((p) => p.user_id === user.user_id);
  const [params] = useSearchParams();
  const invited = LANGS.some((l) => l.code === params.get("lang")) ? params.get("lang") : null;
  const [lang, setLang] = useState(mine?.language || invited || browserLang());
  const full = !mine && room.participants.length >= MAX_PARTICIPANTS;
  const [busy, setBusy] = useState(false);

  const join = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rooms/${room.room_id}/join`, { spoken_language: lang });
      onJoined(data);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-16 sm:py-24" data-testid="room-entry">
      <div className="holo-panel corners matrix-edge p-8 sm:p-12">
        <div className="flex items-center gap-6">
          <AuroraLogo size={64} rings />
          <div>
            <div className="eyebrow">Invitation // {room.room_id}</div>
            <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl"><AuroraWordmark /></h1>
          </div>
        </div>
        <div className="mt-8">
          {room.encrypted ? <NoTraceBanner testId="room-entry-no-trace-banner" /> : (
            <p className="border border-border p-3 font-mono text-[11px] text-muted-foreground" data-testid="room-entry-standard-notice">STANDARD ROOM · transcript and Aurora summary are kept after the call</p>
          )}
        </div>
        <div className="mt-6 grid grid-cols-2 border border-border font-mono text-sm">
          <div className="border-r border-border p-4"><div className="mono-label">Languages in room</div><div className="mt-2 text-lg" data-testid="room-entry-room-langs">{[...new Set(room.participants.map((p) => p.language.toUpperCase()))].join(" · ")}</div></div>
          <div className="p-4"><div className="mono-label">Aurora voice</div><div className="mt-2 text-lg capitalize">{room.voice}</div></div>
        </div>
        <div className="mt-6 flex items-center gap-2 font-mono text-xs text-muted-foreground">
          <Users size={14} /> Host {room.host_name} · {room.participants.length}/{MAX_PARTICIPANTS} people
        </div>
        <label className="mt-8 block">
          <span className="mono-label">Your spoken language</span>
          <div className="mt-2"><LangSelect value={lang} onChange={setLang} allowAuto={false} testId="room-entry-spoken-lang" /></div>
        </label>
        <p className="mt-3 text-xs text-muted-foreground">
          {invited && !mine && !full && <span className="mr-1 text-[color:var(--au-cyan)]" data-testid="room-entry-invited-lang">The host set your language to {langName(invited)}, change it if needed.</span>}
          You will read and hear everyone in {langName(lang)}, and each of them gets your words in their own language.
        </p>
        {full && <p className="mt-4 border border-[color:var(--au-red)] p-3 font-mono text-xs" data-testid="room-entry-full">This room is full ({MAX_PARTICIPANTS}/{MAX_PARTICIPANTS} people).</p>}
        <button onClick={join} disabled={busy || full} className="btn-primary mt-8 w-full" data-testid="room-entry-join-button">
          {busy ? "Connecting…" : "Enter SpeechFlow"} <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};
